import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { createGraphQLRequest } from "../../../../api/utils/httpRequest"
import type {
    CardProgramCardResendEmailData,
    OrderDetail,
    OrderDetailRequestData,
    OrderReportResponse,
    OrdersReportRequestData,
    ReceiptSendRequestData,
    ResponseData,
} from "../../../../graphql/types"

export const constructOrderDetailQuery = (data: OrderDetailRequestData) => {
    return {
        query: `
                query orderDetail($data: OrderDetailRequestData!) {
                    orderDetail(data: $data) {
                        items {
                            id
                            name
                            note
                            unit
                            unitQuantity
                            unitPrice
                            quantity
                            basePrice
                            itemPrice
                            totalPrice
                            option {
                                name
                                price
                                nestedGroups {
                                    name
                                    price
                                    isExclusion
                                    modifiers {
                                        id
                                        name
                                        quantity
                                        price
                                        supplement
                                    }
                                }
                            }
                            modifierGroups {
                                name
                                price
                                isExclusion
                                modifiers {
                                    id
                                    name
                                    quantity
                                    price
                                    supplement
                                    nestedGroups {
                                        name
                                        price
                                        isExclusion
                                        modifiers {
                                            id
                                            name
                                            quantity
                                            price
                                            supplement
                                        }
                                    }
                                }
                            }
                            pricings {
                                name
                                absolute
                                percentage
                                subtotal
                            }
                            cardProgram {
                                cardId
                                cardProgramId
                            }
                        }
                        totals {
                            subtotal
                            pricingsTotal
                            pricings {
                                type
                                name
                                percentage
                                absolute
                                subtotal
                            }
                            taxesTotal
                            taxes {
                                name
                                percentage
                                absolute
                                total
                            }
                            total
                            tip
                            gratuityPercentage
                            gratuityTotal
                            cardSurchargePercentage
                            cardSurchargeTotal
                            cashDiscountPercentage
                            cashDiscountTotal
                            serviceFeePercentage
                            serviceFeeTotal
                            collected
                        }
                        loyalty {
                            accrued
                        }
                        fulfillment {
                            recipient {
                                firstName
                                surname
                                emailAddress
                            }
                        }
                        general {
                            orderNumber
                            orderStatus
                            orderSource
                            onlineOrderingSource
                            paymentStatus
                            assigneeName
                            assignmentName
                            customerName
                            customerEmail
                            customerPhone
                            guestCount
                            timestampOpened
                            timestampClosed
                            timestampAccepted
                            timestampDeclined
                            timestampScheduled
                            timestampExpected
                            timestampReadyEstimated
                            timestampReadyActual
                            locationName
                            locationId
                            note
                            declineReason
                            readyEstimated
                            readyActual
                            pickupEstimated
                            pickupActual
                            deliveryEstimated
                            deliveryActual
                            orderAudit {
                                type
                                serviceName
                                userName
                                timestamp
                                note
                            }
                        }
                        transactions {
                            id
                            type
                            amount
                            transactionNumber
                            status
                            transactorName
                            terminalName
                            paymentType
                            cardLast4
                            externalType
                            timestamp
                            signature
                            paymentRequestId
                            gateway
                            processingDeferred
                        }
                        deliveries {
                            id
                            externalService
                            deliveryState
                            deliveryFee
                            actualPickupTime
                            actualDropoffTime
                            trackingUrl
                            pickupLocation {
                                street
                                unit
                                city
                                state
                                zip
                            }
                            deliveryLocation {
                                street
                                unit
                                city
                                state
                                zip
                            }
                        }
                    }
                }
            `,
        operationName: "orderDetail",
        variables: {
            data: data,
        },
    }
}

export const fetchOrdersReportData = (
    headers: Record<string, any>,
    data: OrdersReportRequestData
): Promise<Maybe<OrderReportResponse>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query ordersReport($data: OrdersReportRequestData!) {
                  ordersReport(data: $data) {
                    data {
                        id
                        assigneeName
                        assignment
                        cardNames
                        externalTypes
                        orderNumber
                        customerName
                        locationId
                        locationName
                        opened
                        closed
                        paymentStatus
                        paymentStatusCode
                        paymentTypes
                        status
                        source
                        tipsAmount
                        gratuityAmount
                        terminalName
                        total
                        deleted                    
                    }
                    totalCount
                  }
                }
                
            `,
            operationName: "ordersReport",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.ordersReport) {
            return response.data.ordersReport
        }

        return null
    })

export const fetchOrderById = (headers: Record<string, any>, id: string): Promise<Maybe<OrderDetail>> => {
    return createGraphQLRequest({
        headers: headers,
        body: constructOrderDetailQuery({
            id: id,
        }),
    })
        .then(response => {
            if (response && response.data && response.data.orderDetail) {
                return response.data.orderDetail
            } else {
                return null
            }
        })
        .catch(() => {
            return null
        })
}

export const orderReceiptSend = (
    headers: Record<string, any>,
    data: ReceiptSendRequestData
): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation receiptSend($data: ReceiptSendRequestData!) {
                    receiptSend(data: $data) {
                      success
                    }
                }
            `,
            operationName: "receiptSend",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        return response.data.receiptSend
    })

export const fetchPaymentRequestDetail = (headers: Record<string, any>, id: string): Promise<Maybe<any>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query paymentRequestDetail($id: String!) {
                    paymentRequestDetail(id: $id) {
                        data
                    }
                }
            `,
            operationName: "paymentRequestDetail",
            variables: {
                id: id,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.paymentRequestDetail) {
            return response.data.paymentRequestDetail.data
        }

        return null
    })

export const resendGiftCardMutation = (
    headers: Record<string, any>,
    data: CardProgramCardResendEmailData
): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation cardProgramCardResendEmail($data: CardProgramCardResendEmailData!) {
                    cardProgramCardResendEmail(data: $data) {
                      success
                    }
                }
            `,
            operationName: "cardProgramCardResendEmail",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        if (response?.data?.cardProgramCardResendEmail?.success) {
            return response.data.cardProgramCardResendEmail
        }

        return null
    })
